import { useEffect, useState } from 'react';
import { api } from '@/api/client';

interface UserSettings {
  email_notifications_enabled: boolean;
}

export function UserSettingsPage() {
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<UserSettings>('/me/settings')
      .then((resp) => {
        if (cancelled) return;
        setSettings(resp.data);
        setError(null);
      })
      .catch((e: { userMessage?: string }) => {
        if (cancelled) return;
        setError(e.userMessage ?? '設定を取得できませんでした');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateEmailNotifications = async (enabled: boolean) => {
    if (!settings) return;
    const previous = settings;
    setSettings({ email_notifications_enabled: enabled });
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const resp = await api.patch<UserSettings>('/me/settings', {
        email_notifications_enabled: enabled,
      });
      setSettings(resp.data);
      setSaved(true);
    } catch (e: unknown) {
      setSettings(previous);
      const maybeError = e as { userMessage?: string };
      setError(maybeError.userMessage ?? '設定を保存できませんでした');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-semibold text-gray-900">ユーザー設定</h1>

      <div className="mt-6 rounded-lg bg-white p-5 shadow-sm">
        {loading ? (
          <p className="text-sm text-gray-500">読み込み中...</p>
        ) : settings ? (
          <div className="flex items-start justify-between gap-6">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">メール通知</h2>
              <p className="mt-1 text-sm text-gray-500">
                フィードバック依頼とコーチ確認依頼の通知を受け取ります。
              </p>
              {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
              {saved && !error && <p className="mt-3 text-sm text-green-700">保存しました</p>}
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={settings.email_notifications_enabled}
                disabled={saving}
                onChange={(e) => updateEmailNotifications(e.target.checked)}
                aria-label="メール通知"
              />
              <span className="h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-blue-600 peer-focus:outline peer-focus:outline-2 peer-focus:outline-offset-2 peer-focus:outline-blue-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-60" />
              <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
            </label>
          </div>
        ) : (
          <p className="text-sm text-red-600">{error ?? '設定を取得できませんでした'}</p>
        )}
      </div>
    </div>
  );
}
